import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faSpinner, faCheckCircle, faTimesCircle, faChevronRight, faChevronDown, faChartLine, faLayerGroup, faDollarSign, faBuilding, faSearch, faFilter, faFileExcel, faFilePdf, faDownload, faPlus, faWrench } from '@fortawesome/free-solid-svg-icons';
import api from '../services/api';
import { useCustomLoading } from '../hooks/useCustomLoading';
import SimulationResultsTable from '../components/SimulationResultsTable';
import MonteCarloResults from '../components/MonteCarloResults';
import { exportSimulationToExcel } from '../utils/excelExport';
import { exportSimulationToPDF as exportSimulationToPDFUtil } from '../utils/pdfExport';

const EnhancedSimulationResults = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // State management - similar to SpanSimulation
  const [bridges, setBridges] = useState([]);
  const [selectedBridgeId, setSelectedBridgeId] = useState('');
  const [selectedBridge, setSelectedBridge] = useState(null); // Full bridge object with level config
  const [selectedSpanIds, setSelectedSpanIds] = useState([]);
  const [isSpanSelectionCollapsed, setIsSpanSelectionCollapsed] = useState(false);
  const [isCalculationDetailsCollapsed, setIsCalculationDetailsCollapsed] = useState(false);
  
  // Simplified hierarchical selection state
  const [selectedPath, setSelectedPath] = useState({}); // { level1: id, level2: id, direction: '...', position: '...' }
  const [hierarchyData, setHierarchyData] = useState({}); // { level1: [...], level2: [...], ... }
  const [selectedElementsCache, setSelectedElementsCache] = useState({}); // Cache for selected elements: { elementId: elementData }
  const [loadingHierarchy, setLoadingHierarchy] = useState(false);

  // Scenario configuration
  const [scenario, setScenario] = useState({
    timeHorizonYears: 30,
    damageSplit: { A: 70, B: 20, C: 10 },
    discountRate: 0.05,
    costYear: new Date().getFullYear()
  });
  
  // Configurable decay threshold (default 2%, range 0.5%-10%+)
  const [decayThreshold, setDecayThreshold] = useState(2.0);
  
  // Configurable maximum consecutive touch-ups (default 2)
  const [maxConsecutiveTouchUps] = useState(2);

  // Monte Carlo configuration
  const [useMonteCarloMode, setUseMonteCarloMode] = useState(false);
  const [monteCarloConfig, setMonteCarloConfig] = useState({
    iterations: 1000,
    costVariation: 15,
    degradationVariation: 20,
    discountRateVariation: 1,
    repairThreshold: 0.3,
    maintenanceThreshold: 0.1
  });

  // Results state
  const [simulationResults, setSimulationResults] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [resultsSearchTerm, setResultsSearchTerm] = useState('');
  const [resultsFilterType, setResultsFilterType] = useState('all');
  const [showDecayPercent, setShowDecayPercent] = useState(false);
  
  // Create Repair Project state
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [projectFormData, setProjectFormData] = useState({
    projectName: '',
    projectCode: '',
    description: '',
    plannedYear: new Date().getFullYear(),
    crewsCount: 1,
    priority: 'medium'
  });

  // Optimization state
  const [showOptimization, setShowOptimization] = useState(false);
  const [optimizationParams, setOptimizationParams] = useState({
    minDecayThreshold: 0.5,
    maxDecayThreshold: 10.0,
    stepSize: 0.5
  });
  const [optimizationResults, setOptimizationResults] = useState(null);
  const [optimizing, setOptimizing] = useState(false);

  // Damage Split Defaults from settings (for showing default hint and prefill)
  const [damageSplitDefaultsFromSettings, setDamageSplitDefaultsFromSettings] = useState([]);
  const hasPrefilledDamageSplitRef = useRef(false);

  // Fetch bridges on mount
  useEffect(() => {
    fetchBridges();
  }, []);

  // Fetch damage split defaults and prefill scenario inputs (once) to match settings
  useEffect(() => {
    const fetchDamageSplitDefaults = async () => {
      try {
        const res = await api.get('/damage-splits?is_active=true');
        if (res.data?.success && Array.isArray(res.data.data) && res.data.data.length > 0) {
          const defaults = res.data.data;
          setDamageSplitDefaultsFromSettings(defaults);
          // Prefill scenario damage split from first default (e.g. C2) so inputs match settings
          if (!hasPrefilledDamageSplitRef.current) {
            hasPrefilledDamageSplitRef.current = true;
            const first = defaults[0];
            setScenario(prev => ({
              ...prev,
              damageSplit: {
                A: Number(first.severityA) || 55,
                B: Number(first.severityB) || 35,
                C: Number(first.severityC) || 10
              }
            }));
          }
        }
      } catch (err) {
        console.error('Error fetching damage split defaults:', err);
      }
    };
    fetchDamageSplitDefaults();
  }, []);

  // Fetch bridge details when selected
  useEffect(() => {
    const fetchBridgeDetails = async () => {
      if (selectedBridgeId) {
        try {
          const response = await api.get(`/bridges/${selectedBridgeId}`);
          if (response.data.success) {
            setSelectedBridge(response.data.data);
          }
        } catch (err) {
          console.error('Error fetching bridge details:', err);
        }
      } else {
        setSelectedBridge(null);
      }
    };
    fetchBridgeDetails();
  }, [selectedBridgeId]);

  // Reset hierarchical selections when bridge changes
  useEffect(() => {
    if (selectedBridgeId) {
      setSelectedPath({});
      setHierarchyData({});
      setSelectedSpanIds([]);
      setSelectedElementsCache({});
    }
  }, [selectedBridgeId]);

  // Fetch hierarchy data when bridge is selected
  useEffect(() => {
    const fetchHierarchy = async () => {
      if (!selectedBridgeId || !selectedBridge) return;

      try {
        setLoadingHierarchy(true);
        
        // Fetch first level (no parent)
        const hierarchyResponse = await api.get(`/span-simulation/hierarchy/${selectedBridgeId}?level=1`);
        if (hierarchyResponse.data.success) {
          setHierarchyData(prev => ({
            ...prev,
            level1: hierarchyResponse.data.data
          }));
        }
        
        // Fetch unique directions from all levels
        const directionsResponse = await api.get(`/span-simulation/directions/${selectedBridgeId}`);
        if (directionsResponse.data.success) {
          setHierarchyData(prev => ({
            ...prev,
            directions: directionsResponse.data.data
          }));
        }
        
        // Fetch unique positions from all levels (independent of direction)
        const positionsResponse = await api.get(`/span-simulation/positions/${selectedBridgeId}`);
        if (positionsResponse.data.success) {
          setHierarchyData(prev => ({
            ...prev,
            positions: positionsResponse.data.data
          }));
        }
      } catch (err) {
        console.error('Error fetching hierarchy:', err);
      } finally {
        setLoadingHierarchy(false);
      }
    };

    fetchHierarchy();
  }, [selectedBridgeId, selectedBridge]);

  // When path changes OR multiple elements are selected, fetch children
  // For multi-select: fetch children for ALL selected elements and merge them
  useEffect(() => {
    const fetchChildren = async () => {
      if (!selectedBridgeId) return;

      const enabledLevels = getEnabledLevels();
      const areaDataLevel = getAreaDataLevel();
      
      // Find highest selected level in path
      let highestLevel = 0;
      let parentId = null;
      
      // Find highest level in path
      for (let i = 1; i <= 8; i++) {
        if (selectedPath[`level${i}`]) {
          highestLevel = i;
          parentId = selectedPath[`level${i}`];
        }
      }
      
      // If we have multiple selected elements at area data level, use them all
      // Otherwise use the single parent from path
      let parentIds = [];
      if (highestLevel === areaDataLevel && selectedSpanIds.length > 0) {
        // Multiple elements selected - use all selected IDs
        parentIds = selectedSpanIds;
      } else if (parentId) {
        // Single parent from path
        parentIds = [parentId];
      }
      
      // Fetch direct children of highest selected level
      // If multiple parents selected, fetch children for all and merge
      if (highestLevel > 0 && parentIds.length > 0) {
        const childLevel = highestLevel + 1;
        
        // Only fetch if next level is enabled and is at or before area data level
        // (Don't fetch beyond area data level as per client requirement)
        if (childLevel <= areaDataLevel && enabledLevels.includes(childLevel)) {
          try {
            setLoadingHierarchy(true);
            
            // For multiple parents, fetch children for each and merge
            const allChildren = [];
            const seenChildIds = new Set();
            
            for (const pid of parentIds) {
              try {
                let url = `/span-simulation/hierarchy/${selectedBridgeId}?level=${childLevel}`;
                url += `&parentId=${pid}`;
                // Apply direction/position filters only at level 1; child levels use parentId only so children are visible
                if (childLevel === 1 && selectedPath.direction) {
                  url += `&direction=${encodeURIComponent(selectedPath.direction)}`;
                }
                if (childLevel === 1 && selectedPath.position) {
                  url += `&position=${encodeURIComponent(selectedPath.position)}`;
                }
                
                const response = await api.get(url);
                if (response.data.success && Array.isArray(response.data.data)) {
                  // Merge children, avoiding duplicates
                  response.data.data.forEach(child => {
                    if (!seenChildIds.has(child.id)) {
                      seenChildIds.add(child.id);
                      allChildren.push(child);
                    }
                  });
                }
              } catch (err) {
                console.error(`Error fetching children for parent ${pid} at level ${childLevel}:`, err);
              }
            }
            
            // Update hierarchy data with merged children (set even when empty so "No items" shows)
            setHierarchyData(prev => ({
              ...prev,
              [`level${childLevel}`]: allChildren
            }));
          } catch (err) {
            console.error(`Error fetching level ${childLevel}:`, err);
          } finally {
            setLoadingHierarchy(false);
          }
        }
      }
      
      // Also ensure area data level is fetched if we have a parent path to it
      // This handles cases where area data level might not be the immediate next level
      const areaDataLevelNum = getAreaDataLevel();
      if (areaDataLevelNum > 0 && enabledLevels.includes(areaDataLevelNum)) {
        // Find the parent of area data level
        let areaDataParentId = null;
        for (let i = areaDataLevelNum - 1; i >= 1; i--) {
          if (selectedPath[`level${i}`]) {
            areaDataParentId = selectedPath[`level${i}`];
            break;
          }
        }
        
        // If we have a parent and area data level isn't already loaded, fetch it
        if (areaDataParentId && !hierarchyData[`level${areaDataLevelNum}`]) {
          try {
            let url = `/span-simulation/hierarchy/${selectedBridgeId}?level=${areaDataLevelNum}`;
            url += `&parentId=${areaDataParentId}`;
            // Direction/position filters only at level 1; deeper levels use parentId only
            if (areaDataLevelNum === 1 && selectedPath.direction) {
              url += `&direction=${encodeURIComponent(selectedPath.direction)}`;
            }
            if (areaDataLevelNum === 1 && selectedPath.position) {
              url += `&position=${encodeURIComponent(selectedPath.position)}`;
            }
            
            const response = await api.get(url);
            if (response.data.success) {
              setHierarchyData(prev => ({
                ...prev,
                [`level${areaDataLevelNum}`]: response.data.data
              }));
            }
          } catch (err) {
            console.error(`Error fetching area data level ${areaDataLevelNum}:`, err);
          }
        }
      }
    };

    fetchChildren();
  }, [selectedPath, selectedBridgeId, selectedSpanIds]); // Include selectedSpanIds to handle multi-select

  // Helper to get enabled levels array
  const getEnabledLevels = () => {
    if (!selectedBridge || !selectedBridge.enabledLevels) return [1, 2, 3];
    const enabledLevels = typeof selectedBridge.enabledLevels === 'string'
      ? JSON.parse(selectedBridge.enabledLevels)
      : selectedBridge.enabledLevels;
    return Array.isArray(enabledLevels) ? enabledLevels : [1, 2, 3];
  };

  // Helper to get level label (user-friendly: "Direction" -> "Item")
  const getLevelLabel = (levelNum) => {
    if (!selectedBridge || !selectedBridge.levelLabels) return `Level ${levelNum}`;
    const levelLabels = typeof selectedBridge.levelLabels === 'string'
      ? JSON.parse(selectedBridge.levelLabels)
      : selectedBridge.levelLabels;
    const levelKey = `level${levelNum}`;
    const label = levelLabels[levelKey]?.label || `Level ${levelNum}`;
    return label === 'Direction' ? 'Item' : label;
  };

  // Helper to find which level holds area data
  const getAreaDataLevel = () => {
    if (!selectedBridge || !selectedBridge.levelLabels) return 2; // Default fallback
    const levelLabels = typeof selectedBridge.levelLabels === 'string'
      ? JSON.parse(selectedBridge.levelLabels)
      : selectedBridge.levelLabels;
    
    for (let i = 1; i <= 8; i++) {
      const levelKey = `level${i}`;
      if (levelLabels[levelKey] && levelLabels[levelKey].holdsAreaData) {
        return i;
      }
    }
    return 2; // Default fallback
  };

  // Total area of selected child-level (area) components for badge
  const selectedAreaSummary = useMemo(() => {
    if (selectedSpanIds.length === 0) return { totalArea: 0, count: 0 };
    const areaDataLevel = getAreaDataLevel();
    const areaLevelKey = `level${areaDataLevel}`;
    let total = 0;
    selectedSpanIds.forEach(id => {
      const el = selectedElementsCache[id] || hierarchyData[areaLevelKey]?.find(e => e.id === id);
      const area = el?.total_area != null ? parseFloat(el.total_area) : 0;
      if (!Number.isNaN(area)) total += area;
    });
    return { totalArea: total, count: selectedSpanIds.length };
  }, [selectedSpanIds, selectedElementsCache, hierarchyData, getAreaDataLevel]);

  // Simple: Click parent → update path → fetch children
  // Direction/Position filters are manual-only (dropdowns); we do not set them from the selected row.
  const handleLevelSelection = (level, elementId) => {
    const areaDataLevel = getAreaDataLevel();
    
    // Don't handle area data level here - that's handled separately
    if (level === areaDataLevel) return;
    
    const newPath = { ...selectedPath };
    
    if (elementId) {
      // Set this level in path only; do not sync direction/position from element (filters are manual-only)
      newPath[`level${level}`] = elementId;
      
      // Clear all levels below this one
      for (let i = level + 1; i <= 8; i++) {
        delete newPath[`level${i}`];
        const newHierarchyData = { ...hierarchyData };
        delete newHierarchyData[`level${i}`];
        setHierarchyData(newHierarchyData);
      }
    } else {
      // Deselecting - clear this and all children
      delete newPath[`level${level}`];
      for (let i = level + 1; i <= 8; i++) {
        delete newPath[`level${i}`];
        const newHierarchyData = { ...hierarchyData };
        delete newHierarchyData[`level${i}`];
        setHierarchyData(newHierarchyData);
      }
      // Clear area selections only if no parent path exists
      if (Object.keys(newPath).length === 0) {
        setSelectedSpanIds([]);
      }
    }
    
    setSelectedPath(newPath);
  };

  // Simple: Toggle area data element selection (multi-select, persists across navigation)
  // Also loads child levels when an element is selected
  const handleAreaDataSelection = (elementId, isChecked) => {
    const areaDataLevel = getAreaDataLevel();
    const areaLevelKey = `level${areaDataLevel}`;
    
    if (isChecked) {
      setSelectedSpanIds(prev => {
        if (!prev.includes(elementId)) {
          return [...prev, elementId];
        }
        return prev;
      });
      
      // Cache the element data so it persists when hierarchy data is cleared
      const element = hierarchyData[areaLevelKey]?.find(el => el.id === elementId);
      if (element) {
        setSelectedElementsCache(prev => ({
          ...prev,
          [elementId]: element
        }));
      }
      
      // Also set in path to trigger child level fetch
      const newPath = { ...selectedPath };
      newPath[`level${areaDataLevel}`] = elementId;
      setSelectedPath(newPath);
    } else {
      setSelectedSpanIds(prev => prev.filter(id => id !== elementId));
      
      // Remove from cache
      setSelectedElementsCache(prev => {
        const newCache = { ...prev };
        delete newCache[elementId];
        return newCache;
      });
      
      // Remove from path if it was the tracked element
      if (selectedPath[`level${areaDataLevel}`] === elementId) {
        const newPath = { ...selectedPath };
        delete newPath[`level${areaDataLevel}`];
        setSelectedPath(newPath);
      }
    }
  };


  const fetchBridges = async () => {
    try {
      const response = await api.get('/bridges?limit=100');
      if (response.data.success) {
        setBridges(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching bridges:', err);
      setError('Failed to load bridges');
    }
  };

  // Note: We no longer fetch all spans upfront - we use hierarchical selection instead

  const handleSelectAll = async () => {
    // Select all elements at the area data level under the current path (so we don't mix PBs from other spans)
    const areaDataLevel = getAreaDataLevel();
    let parentId = null;
    for (let i = areaDataLevel - 1; i >= 1; i--) {
      if (selectedPath[`level${i}`]) {
        parentId = selectedPath[`level${i}`];
        break;
      }
    }
    try {
      let url = `/span-simulation/hierarchy/${selectedBridgeId}?level=${areaDataLevel}`;
      if (parentId) url += `&parentId=${parentId}`;
      if (selectedPath.direction) url += `&direction=${encodeURIComponent(selectedPath.direction)}`;
      if (selectedPath.position) url += `&position=${encodeURIComponent(selectedPath.position)}`;
      const response = await api.get(url);
      if (response.data.success) {
        const allElements = response.data.data;
        const allIds = allElements.map(el => el.id);
        setSelectedSpanIds(allIds);

        const cacheUpdate = {};
        allElements.forEach(element => {
          cacheUpdate[element.id] = {
            id: element.id,
            element_code: element.code || element.element_code,
            element_name: element.name || element.element_name,
            total_area: element.area || element.total_area
          };
        });
        setSelectedElementsCache(prev => ({ ...prev, ...cacheUpdate }));
      }
    } catch (err) {
      console.error('Error selecting all:', err);
    }
  };

  const handleDeselectAll = () => {
    setSelectedSpanIds([]);
    setSelectedElementsCache({});
    // Do not clear selectedPath so the hierarchy and panel bay list stay visible and clickable
  };

  const handleScenarioChange = (field, value) => {
    setScenario(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDamageSplitChange = (level, value) => {
    setScenario(prev => ({
      ...prev,
      damageSplit: {
        ...prev.damageSplit,
        [level]: parseInt(value)
      }
    }));
  };

  const handleCreateRepairProject = async () => {
    if (!projectFormData.projectName || !projectFormData.plannedYear || !selectedBridge) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get elements that have repairs in the selected year (plannedYear)
      const plannedYear = projectFormData.plannedYear;
      const startYear = simulationResults.simulationStartYear != null && simulationResults.simulationStartYear > 0
        ? simulationResults.simulationStartYear
        : new Date().getFullYear();
      const yearIndex = plannedYear - startYear + 1;
      
      // Find the year result for the planned year
      const yearResult = simulationResults.yearlyResults?.find(yr => yr.year === yearIndex);
      
      if (!yearResult || yearResult.totalCost === 0) {
        setError(`No repairs found for year ${plannedYear}. Please select a year with repair costs.`);
        setLoading(false);
        return;
      }

      // Get elements with repairs in this year
      const elementCosts = {}; // Track costs per element

      if (yearResult.spanResults) {
        yearResult.spanResults.forEach(spanResult => {
          if (spanResult.totalCost > 0) {
            if (!elementCosts[spanResult.spanId]) {
              elementCosts[spanResult.spanId] = {
                id: spanResult.spanId,
                code: spanResult.spanCode,
                name: spanResult.spanName,
                totalCost: 0
              };
            }
            elementCosts[spanResult.spanId].totalCost += spanResult.totalCost;
          }
        });
      }

      const elementIds = Object.keys(elementCosts).map(id => parseInt(id));
      
      if (elementIds.length === 0) {
        setError('No elements with repairs found in selected years');
        return;
      }

      // Get bridge ID from the elements (more reliable than selectedBridge)
      // Fetch bridge ID from the first element to ensure we use the correct bridge
      let actualBridgeId = null;
      
      try {
        // Get bridgeId from the first element using the existing API
        const elementResponse = await api.get(`/bridge-structure/${elementIds[0]}`);
        console.log('Element API response:', elementResponse.data);
        if (elementResponse.data && elementResponse.data.success && elementResponse.data.data) {
          const elementData = elementResponse.data.data;
          // Check both possible field names (camelCase from Sequelize, snake_case from raw query)
          if (elementData.bridgeId !== undefined && elementData.bridgeId !== null) {
            actualBridgeId = parseInt(elementData.bridgeId);
          } else if (elementData.bridge_id !== undefined && elementData.bridge_id !== null) {
            actualBridgeId = parseInt(elementData.bridge_id);
          }
        }
      } catch (err) {
        console.error('Error fetching element bridge ID from API:', err);
      }
      
      // Fallback: try to get from simulation results spans
      if (!actualBridgeId && simulationResults.spans && simulationResults.spans.length > 0) {
        const firstSpan = simulationResults.spans[0];
        if (firstSpan.bridgeId !== undefined && firstSpan.bridgeId !== null) {
          actualBridgeId = parseInt(firstSpan.bridgeId);
        } else if (firstSpan.bridge_id !== undefined && firstSpan.bridge_id !== null) {
          actualBridgeId = parseInt(firstSpan.bridge_id);
        }
      }
      
      // Last fallback: use selectedBridge
      if (!actualBridgeId) {
        actualBridgeId = selectedBridge?.id ? parseInt(selectedBridge.id) : null;
        console.warn('Using selected bridge ID as fallback:', actualBridgeId);
      }
      
      if (!actualBridgeId) {
        setError('Could not determine bridge ID. Please ensure elements belong to a bridge.');
        setLoading(false);
        return;
      }
      
      console.log('Using bridge ID:', actualBridgeId, 'for elements:', elementIds);

      // Calculate total estimated cost
      const totalEstimatedCost = Object.values(elementCosts).reduce((sum, el) => sum + el.totalCost, 0);

      // Create repair project
      const projectData = {
        bridgeId: actualBridgeId,
        projectName: projectFormData.projectName,
        projectCode: projectFormData.projectCode || undefined,
        description: projectFormData.description || undefined,
        spanIds: elementIds,
        plannedYear: projectFormData.plannedYear,
        crewsCount: projectFormData.crewsCount,
        priority: projectFormData.priority,
        estimatedCost: totalEstimatedCost,
        notes: `Created from simulation results. Year: ${plannedYear}. Total elements: ${elementIds.length}.`
      };

      const response = await api.post('/repair-projects', projectData);

      if (response.data.success) {
        setShowCreateProjectModal(false);
        setProjectFormData({
          projectName: '',
          projectCode: '',
          description: '',
          plannedYear: new Date().getFullYear(),
          crewsCount: 1,
          priority: 'medium'
        });
        
        // Navigate to the new project or show success
        const projectId = response.data.data.id;
        if (window.confirm(`Repair project created successfully! Would you like to view it?`)) {
          window.location.href = `/repair-projects/${projectId}`;
        }
      } else {
        setError(response.data.message || 'Failed to create repair project');
      }
    } catch (err) {
      console.error('Error creating repair project:', err);
      setError(err.response?.data?.message || 'Failed to create repair project');
    } finally {
      setLoading(false);
    }
  };

  const runOptimization = async () => {
    if (selectedSpanIds.length === 0) {
      setError('Please select at least one span');
      return;
    }

    // Validate damage split totals 100%
    const totalSplit = Object.values(scenario.damageSplit).reduce((sum, val) => sum + val, 0);
    if (totalSplit !== 100) {
      setError(`Damage split must total 100% (currently ${totalSplit}%)`);
      return;
    }

    try {
      setOptimizing(true);
      setError(null);
      setOptimizationResults(null);
      setSimulationResults(null);

      const response = await api.post('/optimization/run', {
        spanIds: selectedSpanIds,
        scenario: scenario,
        optimizationParams: optimizationParams
      });

      if (response.data.success) {
        setOptimizationResults(response.data.data);
        // Set the optimal simulation as the main results
        if (response.data.data.optimalSimulation) {
          const optimalSim = response.data.data.optimalSimulation;
          
          // Transform hierarchy asynchronously (same as regular simulation)
          try {
            const hierarchy = await transformToHierarchy(optimalSim);
            optimalSim.hierarchy = hierarchy;
          } catch (err) {
            console.error('Error transforming hierarchy for optimal simulation:', err);
            optimalSim.hierarchy = null;
          }
          
          setSimulationResults(optimalSim);
        }
      }
    } catch (err) {
      console.error('Error running optimization:', err);
      setError(err.response?.data?.message || 'Failed to run optimization');
    } finally {
      setOptimizing(false);
    }
  };

  const runSimulation = async () => {
    if (selectedSpanIds.length === 0) {
      setError('Please select at least one span');
      return;
    }

    // Warn about large simulations
    if (selectedSpanIds.length > 100) {
      const confirmed = window.confirm(
        `⚠️ Large Simulation Warning\n\n` +
        `You are about to simulate ${selectedSpanIds.length} spans.\n` +
        `This may take 1-3 minutes to complete.\n\n` +
        `Do you want to continue?`
      );
      if (!confirmed) {
        return;
      }
    }

    // Validate damage split totals 100%
    const totalSplit = Object.values(scenario.damageSplit).reduce((sum, val) => sum + val, 0);
    if (totalSplit !== 100) {
      setError(`Damage split must total 100% (currently ${totalSplit}%)`);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSimulationResults(null);
      setExpandedItems(new Set()); // Clear expanded items for new simulation

      const response = await api.post('/span-simulation/run', {
        spanIds: selectedSpanIds,
        scenario: scenario,
        useCustomRepairSteps: false,
        useMonteCarloMode: useMonteCarloMode,
        monteCarloConfig: useMonteCarloMode ? monteCarloConfig : null,
        decayThreshold: decayThreshold,
        maxConsecutiveTouchUps: maxConsecutiveTouchUps
      });

      if (response.data.success) {
        const results = response.data.data;
        
        // Transform hierarchy asynchronously
        try {
          const hierarchy = await transformToHierarchy(results);
          results.hierarchy = hierarchy;
        } catch (err) {
          console.error('Error transforming hierarchy:', err);
          results.hierarchy = null;
        }
        
        setSimulationResults(results);
        
        // Scroll to results only once when simulation completes
        setTimeout(() => {
          const resultsElement = document.getElementById('simulation-results');
          if (resultsElement && !simulationResults) { // Only scroll if this is the first time showing results
            resultsElement.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            });
          }
        }, 100);
      }
    } catch (err) {
      console.error('Error running simulation:', err);
      const msg = err.response?.data?.message || err.response?.data?.error;
      setError(msg || 'Failed to run simulation');
      const main = document.querySelector('main');
      if (main) main.scrollTo({ top: 0, behavior: 'smooth' });
      else window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2
    }).format(value);
  };

  const toggleExpanded = (itemId) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Note: Pagination removed - using hierarchical selection instead

  // Transform simulation results into hierarchy format for accordion display
  const transformToHierarchyFn = async (results) => {
    if (!results) return null;

    // For Monte Carlo, we don't show the hierarchical table (use Monte Carlo component instead)
    if (results.mode === 'monte_carlo') {
      return null;
    }

    const spans = results.spans;
    const yearlyResults = results.yearlyResults;
    
    if (!yearlyResults || !spans || spans.length === 0) return null;

    // Get bridge ID from first span
    const bridgeId = selectedBridgeId;
    if (!bridgeId || !selectedBridge) return null;

    const levelConfig = selectedBridge.levelLabels 
      ? (typeof selectedBridge.levelLabels === 'string' 
          ? JSON.parse(selectedBridge.levelLabels) 
          : selectedBridge.levelLabels)
      : {};

    // Fetch hierarchy structure from backend
    try {
      const elementIds = spans.map(s => s.id);
      const hierarchyResponse = await api.post('/span-simulation/hierarchy-structure', {
        elementIds: elementIds,
        bridgeId: bridgeId
      });

      if (!hierarchyResponse.data.success) {
        console.error('Failed to fetch hierarchy structure');
        return null;
      }

      const { hierarchy, levelConfig: fetchedLevelConfig } = hierarchyResponse.data.data;
      const finalLevelConfig = fetchedLevelConfig || levelConfig;

      // Use simulation start year (Default Year 1 from settings) or current year
      const startYear = results.simulationStartYear != null && results.simulationStartYear > 0
        ? results.simulationStartYear
        : new Date().getFullYear();
      const timeHorizon = results.timeHorizonYears || 30;
      const allYears = [];
      for (let i = 1; i <= timeHorizon; i++) {
        allYears.push(startYear + i - 1);
      }

      // Build hierarchy tree with costs
      const buildHierarchyWithCosts = (elements, parentLevel = 0) => {
        return elements.map(element => {
          const levelLabel = finalLevelConfig[`level${element.levelNumber}`]?.label || `Level ${element.levelNumber}`;
          
          // Simplify name - use code if available, otherwise extract last part of name
          let displayName = element.code || 'Unnamed';
          if (!element.code && element.name) {
            // If name is like "Panel 3 of Pillar 1 of Plinth 1", just use "Panel 3"
            const nameParts = element.name.split(' of ');
            displayName = nameParts[nameParts.length - 1] || element.name;
          } else if (element.code && element.name) {
            // Use code as primary, name as secondary if different
            displayName = element.code;
          }
          
          // Get area from element, defaulting to 0 if not available
          const elementArea = parseFloat(element.area || element.total_area || 0) || 0;
          
          const node = {
            id: element.id, // Store element ID for span result lookup
            type: element.levelNumber === 1 ? 'bridge' : 
                  element.levelNumber === 2 ? 'span' : 
                  element.levelNumber === 3 ? 'panelbay' : 'asset',
            name: displayName,
            code: element.code || '',
            area: elementArea.toString(),
            description: `${levelLabel}: ${displayName}`,
            level: element.levelNumber,
            levelLabel: levelLabel,
            children: [],
            years: []
          };

          // Initialize years array with all years (set to 0 initially)
          allYears.forEach(year => {
            node.years.push({ year, cost: 0, degradationPercent: 0 });
          });

          // Check if this element has structural children (will be determined after building children)
          // We'll set this flag after building children to decide whether to use own cost or sum of children
          let hasStructuralChildren = false;
          
          // IMPORTANT: Only set own cost for nodes that are at the area data level
          // This cost will be REPLACED with sum of children if element has structural children
          // Only leaf nodes (no structural children) should keep their own span cost
          const matchingSpan = spans.find(s => s.id === element.id);
          if (matchingSpan) {
            // Add yearly costs for this element (temporarily, will be replaced if has children)
            yearlyResults.forEach(yearResult => {
              const spanResult = yearResult.spanResults.find(sr => sr.spanId === element.id);
              if (spanResult) {
                const year = startYear + yearResult.year - 1;
                const yearIndex = node.years.findIndex(y => y.year === year);
                if (yearIndex >= 0) {
                  node.years[yearIndex].cost = spanResult.totalCost || 0;
                  node.years[yearIndex].degradationPercent = spanResult.degradationPercent || 0;
                  // Debug logging
                  if (spanResult.totalCost > 0) {
                    console.log(`Added cost for ${node.name} (${element.id}) year ${year}: $${spanResult.totalCost}`);
                  }
                }
              }
            });
            
            // Debug: log if no costs found
            const totalCost = node.years.reduce((sum, y) => sum + y.cost, 0);
            if (totalCost === 0) {
              console.warn(`No costs found for element ${node.name} (ID: ${element.id}). Span data:`, matchingSpan);
              console.warn(`Yearly results for this span:`, yearlyResults.map(yr => ({
                year: yr.year,
                spanResult: yr.spanResults.find(sr => sr.spanId === element.id)
              })));
            }

            // Add repair processes as children
            const firstYearResult = yearlyResults[0]?.spanResults.find(sr => sr.spanId === element.id);
            if (firstYearResult) {
              const severityLevels = ['A', 'B', 'C'];
              
              severityLevels.forEach(severity => {
                const processes = firstYearResult[`repairProcesses${severity}`] || [];
                if (processes.length > 0) {
                  const severityNode = {
                    id: element.id, // Keep parent span ID for lookup
                    type: 'maintenance',
                    name: `Paint ${matchingSpan.corrosionCategory || 'C2'} - Severity ${severity}`,
                    description: `Maintenance for ${severity} level damage`,
                    children: [],
                    years: []
                  };

                  // Initialize years for severity node
                  allYears.forEach(year => {
                    severityNode.years.push({ year, cost: 0, degradationPercent: 0 });
                  });

                  processes.forEach(process => {
                    const activityNode = {
                      id: element.id, // Keep parent span ID for lookup
                      type: 'activity',
                      name: process.processName || process.name,
                      description: `${process.processName || process.name} process`,
                      years: []
                    };

                    // Initialize years for activity node
                    allYears.forEach(year => {
                      activityNode.years.push({ year, cost: 0, degradationPercent: 0 });
                    });

                    yearlyResults.forEach(yearResult => {
                      const spanResult = yearResult.spanResults.find(sr => sr.spanId === element.id);
                      if (spanResult) {
                        const totalCostForSeverity = spanResult[`cost${severity}`] || 0;
                        const totalSequenceOrder = processes.reduce((sum, p) => sum + (p.sequenceOrder || 1), 0);
                        const processWeight = totalSequenceOrder > 0 ? (process.sequenceOrder || 1) / totalSequenceOrder : 1 / processes.length;
                        const processCost = totalCostForSeverity * processWeight;
                        
                        const year = startYear + yearResult.year - 1;
                        const yearIndex = activityNode.years.findIndex(y => y.year === year);
                        if (yearIndex >= 0 && processCost > 0) {
                          activityNode.years[yearIndex].cost = Math.round(processCost * 100) / 100;
                        }
                      }
                    });

                    // Add activity if it has any costs
                    const hasCosts = activityNode.years.some(y => y.cost > 0);
                    if (hasCosts) {
                      severityNode.children.push(activityNode);
                      
                      // Aggregate activity costs to severity node
                      activityNode.years.forEach((activityYear, idx) => {
                        if (severityNode.years[idx]) {
                          severityNode.years[idx].cost += activityYear.cost;
                        }
                      });
                    }
                  });

                  // Only add severity node if it has activities
                  if (severityNode.children.length > 0) {
                    node.children.push(severityNode);
                    
                    // NOTE: Do NOT add maintenance/activity costs to parent node here
                    // Maintenance/activity costs are for leaf nodes only (nodes with no structural children)
                    // If the node has structural children, the parent cost will be replaced with sum of structural children
                    // We'll handle maintenance costs after we know if there are structural children (see below)
                  }
                }
              });
            }
          }

          // Recursively build children
          if (element.children && element.children.length > 0) {
            const childNodes = buildHierarchyWithCosts(element.children, element.levelNumber);
            
            // Separate structural children (actual bridge elements) from maintenance/activity children
            const structuralChildren = childNodes.filter(child => 
              child.type !== 'maintenance' && child.type !== 'activity'
            );
            const nonStructuralChildren = childNodes.filter(child => 
              child.type === 'maintenance' || child.type === 'activity'
            );
            
            // Set flag for whether this node has structural children
            hasStructuralChildren = structuralChildren.length > 0;
            
            // Add all children (structural + maintenance/activity)
            node.children = [...nonStructuralChildren, ...structuralChildren];
            
            // CRITICAL: If node has structural children, parent cost MUST equal sum of children
            // This ensures parent totals always match sum of direct children, regardless of own span cost
            if (structuralChildren.length > 0) {
              // ALWAYS replace parent costs with sum of structural children for each year
              // This overrides any own span cost - parent nodes should never show their own cost
              // Only leaf nodes (no structural children) should show their own span cost
              node.years.forEach((parentYear, idx) => {
                const childrenSum = structuralChildren.reduce((sum, child) => {
                  const childYear = child.years[idx];
                  return sum + (childYear ? childYear.cost : 0);
                }, 0);
                // Replace parent cost with sum of children (ignore own span cost completely)
                parentYear.cost = childrenSum;
              });
              
              // Debug: Log if parent cost doesn't match sum of children (should never happen after fix)
              node.years.forEach((parentYear, idx) => {
                const childrenSum = structuralChildren.reduce((sum, child) => {
                  const childYear = child.years[idx];
                  return sum + (childYear ? childYear.cost : 0);
                }, 0);
                if (Math.abs(parentYear.cost - childrenSum) > 0.01) {
                  console.warn(`Cost mismatch for ${node.name} year ${parentYear.year}: parent=${parentYear.cost}, children sum=${childrenSum}`);
                  console.warn(`  Structural children:`, structuralChildren.map(c => ({
                    name: c.name,
                    cost: c.years[idx]?.cost || 0
                  })));
                } else {
                  // Log successful aggregation for debugging
                  console.log(`✓ ${node.name} year ${parentYear.year}: parent=${parentYear.cost}, children sum=${childrenSum}`);
                }
              });
            } else {
              // If no structural children, this is a leaf node (area data level)
              // Keep the node's own span cost (already set above)
              // Aggregate maintenance/activity children costs (additive to own span cost)
              // This is for leaf nodes that only have maintenance/activity children
              nonStructuralChildren.forEach(child => {
                child.years.forEach((childYear, idx) => {
                  if (node.years[idx]) {
                    node.years[idx].cost += childYear.cost;
                  }
                });
              });
            }
            
            // Aggregate areas from children to parent
            // Sum up all child areas (only count direct structural children, not repair process children)
            const directChildNodes = childNodes.filter(child => 
              child.type !== 'maintenance' && child.type !== 'activity'
            );
            if (directChildNodes.length > 0) {
              const totalChildArea = directChildNodes.reduce((sum, child) => {
                const childArea = parseFloat(child.area) || 0;
                return sum + childArea;
              }, 0);
              
              // Always use sum of children for parent area (children are more granular and accurate)
              // This ensures parent areas reflect the sum of their children
              if (totalChildArea > 0) {
                node.area = totalChildArea.toString();
              }
            }
          }

          return node;
        });
      };

      const hierarchyTree = buildHierarchyWithCosts(hierarchy);
      
      // Wrap in bridge node if needed
      if (hierarchyTree.length === 1 && hierarchyTree[0].level === 1) {
        return hierarchyTree[0];
      }

      // Create bridge wrapper
      const bridgeNode = {
        type: 'bridge',
        name: selectedBridge.name || 'Main Bridge',
        code: selectedBridge.bridgeCode || 'BR-001',
        area: spans.reduce((sum, span) => sum + parseFloat(span.totalArea || span.total_area || 0), 0).toString(),
        description: 'Main bridge structure',
        children: hierarchyTree,
        years: []
      };

      // Initialize years for bridge node
      allYears.forEach(year => {
        bridgeNode.years.push({ year, cost: 0, degradationPercent: 0 });
      });

      // Aggregate costs from all children to bridge node
      hierarchyTree.forEach(child => {
        child.years.forEach((childYear, idx) => {
          if (bridgeNode.years[idx]) {
            bridgeNode.years[idx].cost += childYear.cost;
          }
        });
      });
      
      // Aggregate areas from all children to bridge node
      // Calculate total area from all direct children (excluding repair process children)
      const directChildren = hierarchyTree.filter(child => 
        child.type !== 'maintenance' && child.type !== 'activity'
      );
      if (directChildren.length > 0) {
        const totalChildArea = directChildren.reduce((sum, child) => {
          const childArea = parseFloat(child.area) || 0;
          return sum + childArea;
        }, 0);
        
        // Update bridge area if children have area data
        if (totalChildArea > 0) {
          bridgeNode.area = totalChildArea.toString();
        }
      }

      return bridgeNode;
    } catch (error) {
      console.error('Error fetching hierarchy structure:', error);
      // Fallback to simple structure
      return null;
    }
  };
  
  const transformToHierarchy = useCallback(transformToHierarchyFn, [selectedBridgeId, selectedBridge]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Enhanced Simulation Results</h1>
        <p className="text-sm text-gray-600">Run detailed degradation and cost simulations with hierarchical results display</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          <FontAwesomeIcon icon={faTimesCircle} className="mr-2" />
          {error}
        </div>
      )}

      {/* Bridge Selection */}
      <div className="bg-white rounded-lg shadow-sm border mb-6">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">1. Select Bridge</h2>
          <div className="max-w-md">
            <label className="block text-sm font-medium text-gray-700 mb-2">Bridge</label>
            <select
              value={selectedBridgeId}
              onChange={(e) => setSelectedBridgeId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">-- Select a bridge --</option>
              {bridges.map(bridge => (
                <option key={bridge.id} value={bridge.id}>
                  {bridge.bridgeName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Span Selection */}
      {selectedBridgeId && (
        <div className="bg-white rounded-lg shadow-sm border mb-6">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <button 
                  className="mr-3 text-gray-500 hover:text-gray-700"
                  onClick={() => setIsSpanSelectionCollapsed(!isSpanSelectionCollapsed)}
                  title={isSpanSelectionCollapsed ? 'Expand span selection' : 'Collapse span selection'}
                >
                  <FontAwesomeIcon icon={isSpanSelectionCollapsed ? faChevronRight : faChevronDown} />
                </button>
                <h2 className="text-lg font-semibold text-gray-900">2. Select Elements</h2>
                {isSpanSelectionCollapsed && (
                  <span className="ml-3 text-sm text-gray-600">
                    ({selectedSpanIds.length} component{selectedSpanIds.length !== 1 ? 's' : ''} selected)
                    {selectedSpanIds.length > 100 && (
                      <span className="ml-2 text-yellow-600">
                        ⚠️ Large simulation
                      </span>
                    )}
                  </span>
                )}
              </div>
              {!isSpanSelectionCollapsed && (
                <div className="flex items-center space-x-3">
                  <button 
                    onClick={handleSelectAll} 
                    className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                    disabled={!selectedBridgeId}
                  >
                    Select All
                  </button>
                  <button 
                    onClick={handleDeselectAll} 
                    className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                    disabled={selectedSpanIds.length === 0}
                  >
                    Deselect All
                  </button>
                  <span className="text-sm text-gray-600">
                    {selectedSpanIds.length} component{selectedSpanIds.length !== 1 ? 's' : ''} selected
                  </span>
                </div>
              )}
            </div>

            {!isSpanSelectionCollapsed && (
              <>
                {/* Hierarchical Cascading Selection with Listboxes */}
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-4">
                    Select elements through the hierarchy. Click on elements in each column to navigate. Simulation will run on elements at the level that holds area data.
                  </p>
                  
                  {/* Top Level Filters */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    {/* Item (formerly Direction) Filter */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Item</label>
                      <select
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={selectedPath.direction || ''}
                        onChange={async (e) => {
                          const newPath = { ...selectedPath };
                          if (e.target.value) {
                            newPath.direction = e.target.value;
                          } else {
                            delete newPath.direction;
                          }
                          // Clear all level selections when direction changes (keep directions & positions for dropdowns)
                          Object.keys(newPath).forEach(key => {
                            if (key.startsWith('level')) delete newPath[key];
                          });
                          setSelectedPath(newPath);
                          // Clear only level data; preserve directions and positions so dropdowns keep working
                          setHierarchyData(prev => {
                            const next = { ...prev };
                            for (let i = 1; i <= 8; i++) delete next[`level${i}`];
                            return next;
                          });
                          
                          // Re-fetch level 1 with new direction filter
                          if (selectedBridgeId) {
                            try {
                              setLoadingHierarchy(true);
                              let url = `/span-simulation/hierarchy/${selectedBridgeId}?level=1`;
                              if (e.target.value) {
                                url += `&direction=${encodeURIComponent(e.target.value)}`;
                              }
                              const response = await api.get(url);
                              if (response.data.success) {
                                const elements = response.data.data;
                                setHierarchyData(prev => ({
                                  ...prev,
                                  level1: elements
                                }));
                                
                                // Update positions based on filtered direction
                                if (e.target.value) {
                                  const positionsResponse = await api.get(
                                    `/span-simulation/positions/${selectedBridgeId}?direction=${encodeURIComponent(e.target.value)}`
                                  );
                                  if (positionsResponse.data.success) {
                                    setHierarchyData(prev => ({
                                      ...prev,
                                      positions: positionsResponse.data.data
                                    }));
                                  }
                                } else {
                                  const positionsResponse = await api.get(`/span-simulation/positions/${selectedBridgeId}`);
                                  if (positionsResponse.data.success) {
                                    setHierarchyData(prev => ({
                                      ...prev,
                                      positions: positionsResponse.data.data
                                    }));
                                  }
                                }
                              }
                            } catch (err) {
                              console.error('Error fetching filtered hierarchy:', err);
                            } finally {
                              setLoadingHierarchy(false);
                            }
                          }
                        }}
                      >
                        <option value="">All Items</option>
                        {hierarchyData.directions?.map(dir => (
                          <option key={dir} value={dir}>{dir}</option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Position Filter (Internal / External) */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Position</label>
                      <select
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={selectedPath.position || ''}
                        onChange={async (e) => {
                          const newPath = { ...selectedPath };
                          if (e.target.value) {
                            newPath.position = e.target.value;
                          } else {
                            delete newPath.position;
                          }
                          // Clear all level selections when position changes (keep directions & positions for dropdowns)
                          Object.keys(newPath).forEach(key => {
                            if (key.startsWith('level')) delete newPath[key];
                          });
                          setSelectedPath(newPath);
                          // Clear only level data; preserve directions and positions so dropdowns keep working
                          setHierarchyData(prev => {
                            const next = { ...prev };
                            for (let i = 1; i <= 8; i++) delete next[`level${i}`];
                            return next;
                          });
                          
                          // Re-fetch level 1 with new position filter
                          if (selectedBridgeId) {
                            try {
                              setLoadingHierarchy(true);
                              let url = `/span-simulation/hierarchy/${selectedBridgeId}?level=1`;
                              if (newPath.direction) {
                                url += `&direction=${encodeURIComponent(newPath.direction)}`;
                              }
                              if (e.target.value) {
                                url += `&position=${encodeURIComponent(e.target.value)}`;
                              }
                              const response = await api.get(url);
                              if (response.data.success) {
                                const elements = response.data.data;
                                setHierarchyData(prev => ({
                                  ...prev,
                                  level1: elements
                                }));
                              }
                            } catch (err) {
                              console.error('Error fetching filtered hierarchy:', err);
                            } finally {
                              setLoadingHierarchy(false);
                            }
                          }
                        }}
                        disabled={false}
                      >
                        <option value="">All Positions</option>
                        {hierarchyData.positions?.map(pos => {
                          const displayPos = (pos === 'Internal' || pos === 'External') ? pos : (String(pos).replace(/^P\d+\s*/i, '').trim() || pos);
                          return (
                            <option key={pos} value={pos}>{displayPos}</option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                  
                  {/* Hierarchical Columns - Listboxes */}
                  <div className="flex gap-2 overflow-x-auto pb-4">
                    {getEnabledLevels()
                      .filter(levelNum => levelNum <= getAreaDataLevel())
                      .map((levelNum) => {
                      const levelKey = `level${levelNum}`;
                      const levelLabel = getLevelLabel(levelNum);
                      const isAreaDataLevel = levelNum === getAreaDataLevel();
                      const elements = hierarchyData[levelKey] || [];
                      const selectedId = selectedPath[levelKey] || null;
                      
                      // Simple: Level 1 always enabled, others need parent selected
                      const isLevelEnabled = levelNum === 1 ? true : !!selectedPath[`level${levelNum - 1}`];
                      
                      return (
                        <div key={levelNum} className="flex-shrink-0" style={{ minWidth: '140px', maxWidth: '140px' }}>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            {levelLabel}
                            {isAreaDataLevel && (
                              <span className="ml-1 text-xs text-blue-600 font-normal">
                                (Area)
                              </span>
                            )}
                          </label>
                          <div 
                            className="border border-gray-300 rounded-md bg-white"
                            style={{ height: isAreaDataLevel ? '400px' : '300px', overflowY: 'auto' }}
                          >
                            {loadingHierarchy && elements.length === 0 ? (
                              <div className="p-2 text-center text-gray-500 text-xs">
                                <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
                                Loading...
                              </div>
                            ) : elements.length === 0 ? (
                              <div className="p-2 text-center text-gray-400 text-xs">
                                {isLevelEnabled ? 'No items' : 'Select parent'}
                              </div>
                            ) : (
                              <div className="divide-y divide-gray-200">
                                {elements.map(element => {
                                  // For area data level, use multi-select with checkboxes
                                  if (isAreaDataLevel) {
                                    const isSelected = selectedSpanIds.includes(element.id);
                                    const displayName = element.element_name || element.element_code;
                                    return (
                                      <div
                                        key={element.id}
                                        className={`py-0.5 px-1 cursor-pointer hover:bg-gray-50 transition-colors flex items-center gap-2 ${
                                          isSelected ? 'bg-blue-100 border-l-2 border-blue-500' : ''
                                        }`}
                                        onClick={() => {
                                          if (isLevelEnabled) {
                                            handleAreaDataSelection(element.id, !isSelected);
                                          }
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() => {}}
                                          onClick={(e) => e.stopPropagation()}
                                          className="cursor-pointer flex-shrink-0"
                                          disabled={!isLevelEnabled}
                                        />
                                        <div className="flex-1 min-w-0 font-medium text-xs text-gray-900 truncate" title={displayName}>
                                          {displayName}
                                        </div>
                                      </div>
                                    );
                                  } else {
                                    // For navigation levels, use single selection; show element name only (user-friendly)
                                    const isSelected = selectedId === element.id;
                                    const displayName = element.element_name || element.element_code || `ID ${element.id}`;
                                    return (
                                      <div
                                        key={element.id}
                                        className={`p-1 cursor-pointer hover:bg-gray-50 transition-colors ${
                                          isSelected ? 'bg-blue-100 border-l-2 border-blue-500' : ''
                                        }`}
                                        onClick={() => {
                                          if (isLevelEnabled) {
                                            handleLevelSelection(levelNum, isSelected ? null : element.id);
                                          }
                                        }}
                                      >
                                        <div className="font-medium text-xs text-gray-900 truncate" title={displayName}>
                                          {displayName}
                                        </div>
                                      </div>
                                    );
                                  }
                                })}
                              </div>
                            )}
                          </div>
                          {elements.length > 0 && (
                            <div className="mt-0.5 text-xs text-gray-500 text-center">
                              {elements.length}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Selection Summary */}
                  <div className="mt-4 bg-gray-50 border border-gray-300 rounded-md p-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Selection Summary</h3>
                    <div className="space-y-2 text-sm">
                      {getEnabledLevels()
                        .filter(levelNum => levelNum <= getAreaDataLevel())
                        .map((levelNum) => {
                        const levelKey = `level${levelNum}`;
                        const levelLabel = getLevelLabel(levelNum);
                        const selectedId = selectedPath[levelKey];
                        if (!selectedId) return null;
                        const element = hierarchyData[levelKey]?.find(el => el.id === selectedId);
                        const name = element ? (element.element_name || element.element_code) : 'Selected';
                        return (
                          <div key={levelNum} className="flex items-center justify-between">
                            <span className="text-gray-600">{levelLabel}:</span>
                            <span className="font-medium text-gray-900">{name}</span>
                          </div>
                        );
                      })}
                      
                      {selectedSpanIds.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-300">
                          <div className="text-gray-600 mb-2">Components for Simulation:</div>
                          <div className="space-y-1 max-h-48 overflow-y-auto">
                            {[...selectedSpanIds]
                              .map(spanId => {
                                const areaDataLevel = getAreaDataLevel();
                                const areaLevelKey = `level${areaDataLevel}`;
                                let el = hierarchyData[areaLevelKey]?.find(e => e.id === spanId);
                                if (!el) el = selectedElementsCache[spanId];
                                return { spanId, element: el };
                              })
                              .sort((a, b) => {
                                const codeA = a.element?.element_code ?? a.element?.element_name ?? String(a.spanId);
                                const codeB = b.element?.element_code ?? b.element?.element_name ?? String(b.spanId);
                                return String(codeA).localeCompare(String(codeB), undefined, { numeric: true });
                              })
                              .map(({ spanId, element }) => {
                                if (!element) {
                                  return (
                                    <div key={spanId} className="text-xs text-gray-500">
                                      ID: {spanId} (loading...)
                                    </div>
                                  );
                                }
                                const name = element.element_name || element.element_code;
                                return (
                                  <div key={spanId} className="text-xs flex items-center justify-between bg-blue-50 p-2 rounded">
                                    <span className="font-medium text-gray-900">{name}</span>
                                    <span className="text-gray-600 ml-2">
                                      {element.total_area ? `${parseFloat(element.total_area).toFixed(2)} m²` : 'No area'}
                                    </span>
                                  </div>
                                );
                              })}
                          </div>
                          <div className="mt-2 text-xs text-gray-500">
                            Total: {selectedSpanIds.length} component{selectedSpanIds.length !== 1 ? 's' : ''} selected
                          </div>
                        </div>
                      )}
                    </div>
                    {selectedSpanIds.length > 100 && (
                      <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-md p-3 text-sm text-yellow-800">
                        ⚠️ Large simulation: {selectedSpanIds.length} components selected
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Scenario Configuration */}
      {selectedSpanIds.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border mb-6">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <button 
                  className="mr-3 text-gray-500 hover:text-gray-700"
                  onClick={() => setIsCalculationDetailsCollapsed(!isCalculationDetailsCollapsed)}
                  title={isCalculationDetailsCollapsed ? 'Expand scenario configuration' : 'Collapse scenario configuration'}
                >
                  <FontAwesomeIcon icon={isCalculationDetailsCollapsed ? faChevronRight : faChevronDown} />
                </button>
                <h2 className="text-lg font-semibold text-gray-900">3. Configure Scenario</h2>
              </div>
            </div>

            {!isCalculationDetailsCollapsed && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Time Horizon */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Time Horizon (Years)</label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={scenario.timeHorizonYears}
                    onChange={(e) => handleScenarioChange('timeHorizonYears', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Inflation Rate */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Inflation Rate (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="20"
                    step="0.1"
                    value={scenario.discountRate * 100}
                    onChange={(e) => handleScenarioChange('discountRate', parseFloat(e.target.value) / 100)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Cost Year */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Cost Year</label>
                  <input
                    type="number"
                    min="2020"
                    max="2030"
                    value={scenario.costYear}
                    onChange={(e) => handleScenarioChange('costYear', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Decay Threshold (%) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Decay Threshold (%)</label>
                  <input
                    type="number"
                    min="0.5"
                    max="10"
                    step="0.1"
                    value={decayThreshold}
                    onChange={(e) => setDecayThreshold(parseFloat(e.target.value) || 2.0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">Repairs trigger when decay reaches this threshold (default: 2%)</p>
                </div>
              </div>
            )}

            {/* Damage Split */}
            {!isCalculationDetailsCollapsed && (
              <div className="mt-6">
                <h3 className="text-md font-medium text-gray-900 mb-3">Damage Split (%)</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Severity A</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={scenario.damageSplit.A}
                      onChange={(e) => handleDamageSplitChange('A', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Severity B</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={scenario.damageSplit.B}
                      onChange={(e) => handleDamageSplitChange('B', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Severity C</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={scenario.damageSplit.C}
                      onChange={(e) => handleDamageSplitChange('C', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  Total: {Object.values(scenario.damageSplit).reduce((sum, val) => sum + val, 0)}%
                  {Object.values(scenario.damageSplit).reduce((sum, val) => sum + val, 0) !== 100 && (
                    <span className="text-red-600 ml-2">(Must equal 100%)</span>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {damageSplitDefaultsFromSettings.length > 0 ? (
                    <>
                      Default from settings (Damage Split Defaults): e.g. {damageSplitDefaultsFromSettings.slice(0, 3).map((d, i) => (
                        <span key={d.id != null ? d.id : `${d.corrosionCategory}-${i}`} className="mr-2">
                          {d.corrosionCategory} A={Number(d.severityA)}%, B={Number(d.severityB)}%, C={Number(d.severityC)}%
                        </span>
                      ))}
                      . Simulation uses per-category values from these settings.
                    </>
                  ) : (
                    'Default from settings: A=55%, B=35%, C=10% (e.g. C2). Simulation uses per-category values from Corrosion & Damage → Damage Split Defaults.'
                  )}
                </p>
              </div>
            )}

            {/* Monte Carlo Configuration */}
            {!isCalculationDetailsCollapsed && (
              <div className="mt-6 p-6 bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-lg">
                <label className="flex items-center cursor-pointer mb-4">
                  <input
                    type="checkbox"
                    checked={useMonteCarloMode}
                    onChange={(e) => setUseMonteCarloMode(e.target.checked)}
                    className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <span className="ml-3 text-lg font-semibold text-purple-900">
                    🎲 Enable Monte Carlo Simulation (Risk Analysis)
                  </span>
                </label>
                <p className="text-sm text-purple-700 mb-4">
                  Run probabilistic simulations to analyze cost variability and budget risk with confidence intervals.
                  {useMonteCarloMode 
                    ? ' Monte Carlo mode enabled - will run multiple iterations with randomized parameters.' 
                    : ' Enable to see P10, P50, P90 budget scenarios and risk assessment.'}
                </p>

                {useMonteCarloMode && (
                  <div className="p-4 bg-white rounded-lg border border-purple-100">
                    <h4 className="font-semibold text-gray-900 mb-4">Monte Carlo Parameters</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Number of Iterations
                        </label>
                        <select
                          value={monteCarloConfig.iterations}
                          onChange={(e) => setMonteCarloConfig(prev => ({ ...prev, iterations: parseInt(e.target.value) }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                        >
                          <option value="100">100 (Quick Test)</option>
                          <option value="500">500 (Fast)</option>
                          <option value="1000">1,000 (Standard)</option>
                          <option value="2000">2,000 (Detailed)</option>
                          <option value="5000">5,000 (High Precision)</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">More iterations = more accurate but slower</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Cost Variation (±%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="50"
                          step="1"
                          value={monteCarloConfig.costVariation}
                          onChange={(e) => setMonteCarloConfig(prev => ({ ...prev, costVariation: parseFloat(e.target.value) }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">Materials & labor uncertainty</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Degradation Variation (±%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="50"
                          step="1"
                          value={monteCarloConfig.degradationVariation}
                          onChange={(e) => setMonteCarloConfig(prev => ({ ...prev, degradationVariation: parseFloat(e.target.value) }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">Coating life uncertainty</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Inflation Rate Variation (±%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="5"
                          step="0.1"
                          value={monteCarloConfig.discountRateVariation}
                          onChange={(e) => setMonteCarloConfig(prev => ({ ...prev, discountRateVariation: parseFloat(e.target.value) }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">Economic uncertainty</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Repair Threshold (%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={monteCarloConfig.repairThreshold * 100}
                          onChange={(e) => setMonteCarloConfig(prev => ({ ...prev, repairThreshold: parseFloat(e.target.value) / 100 }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">Trigger full repair at degradation %</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Maintenance Threshold (%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={monteCarloConfig.maintenanceThreshold * 100}
                          onChange={(e) => setMonteCarloConfig(prev => ({ ...prev, maintenanceThreshold: parseFloat(e.target.value) / 100 }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">Trigger maintenance at degradation %</p>
                      </div>
                    </div>

                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                      <strong>📊 Monte Carlo Analysis:</strong> Will run {monteCarloConfig.iterations.toLocaleString()} simulations 
                      with randomized parameters to generate cost distribution curves and confidence intervals (P10, P50, P90).
                      {selectedSpanIds.length > 5 && monteCarloConfig.iterations >= 2000 && (
                        <span className="block mt-2 text-yellow-700">
                          ⚠️ Warning: High iteration count with {selectedSpanIds.length} spans may take 2-5 minutes.
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Optimization Toggle */}
            <div className="mt-6 mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={showOptimization}
                  onChange={(e) => setShowOptimization(e.target.checked)}
                  className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700">Enable Optimization Mode</span>
              </label>
              <p className="text-xs text-gray-500 ml-6 mt-1">
                Find the cheapest 30-year maintenance path by varying decay trigger percentages
              </p>
            </div>

            {/* Optimization Parameters */}
            {showOptimization && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Optimization Parameters</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Min Threshold (%)</label>
                    <input
                      type="number"
                      value={optimizationParams.minDecayThreshold}
                      onChange={(e) => setOptimizationParams(prev => ({ ...prev, minDecayThreshold: parseFloat(e.target.value) || 0.5 }))}
                      min="0.5"
                      max="10"
                      step="0.1"
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Max Threshold (%)</label>
                    <input
                      type="number"
                      value={optimizationParams.maxDecayThreshold}
                      onChange={(e) => setOptimizationParams(prev => ({ ...prev, maxDecayThreshold: parseFloat(e.target.value) || 10.0 }))}
                      min="0.5"
                      max="10"
                      step="0.1"
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Step Size (%)</label>
                    <input
                      type="number"
                      value={optimizationParams.stepSize}
                      onChange={(e) => setOptimizationParams(prev => ({ ...prev, stepSize: parseFloat(e.target.value) || 0.5 }))}
                      min="0.1"
                      max="2"
                      step="0.1"
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  Will test {Math.ceil((optimizationParams.maxDecayThreshold - optimizationParams.minDecayThreshold) / optimizationParams.stepSize) + 1} threshold values
                </p>
              </div>
            )}

            {/* Run Simulation/Optimization Button */}
            <div className="mt-6">
              <button
                onClick={showOptimization ? runOptimization : runSimulation}
                disabled={loading || optimizing || selectedSpanIds.length === 0}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    {useMonteCarloMode 
                      ? `Running Monte Carlo (${monteCarloConfig.iterations} iterations)...` 
                      : 'Running Simulation...'}
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPlay} className="mr-2" />
                    {useMonteCarloMode 
                      ? `🎲 Run Monte Carlo Simulation (${monteCarloConfig.iterations} iterations)` 
                      : showOptimization ? 'Run Optimization' : 'Run Simulation'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Optimization Results */}
      {optimizationResults && (
        <div id="optimization-results" className="space-y-6 mb-6">
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg shadow-sm border border-green-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Optimization Results</h2>
              <span className="px-3 py-1 bg-green-600 text-white text-sm font-semibold rounded-full">
                Optimal Found
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white p-4 rounded-lg shadow">
                <p className="text-xs text-gray-600 mb-1">Optimal Threshold</p>
                <p className="text-2xl font-bold text-blue-600">{optimizationResults.optimalThreshold}%</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <p className="text-xs text-gray-600 mb-1">Optimal NPV</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(optimizationResults.optimalNPV)}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <p className="text-xs text-gray-600 mb-1">Total Cost</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(optimizationResults.optimalTotalCost)}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <p className="text-xs text-gray-600 mb-1">Repair Count</p>
                <p className="text-2xl font-bold text-purple-600">{optimizationResults.optimalRepairCount}</p>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow mb-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Cost Savings Analysis</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Average NPV</p>
                  <p className="font-semibold">{formatCurrency(optimizationResults.statistics.averageNPV)}</p>
                </div>
                <div>
                  <p className="text-gray-600">Min NPV</p>
                  <p className="font-semibold text-green-600">{formatCurrency(optimizationResults.statistics.minNPV)}</p>
                </div>
                <div>
                  <p className="text-gray-600">Max NPV</p>
                  <p className="font-semibold text-red-600">{formatCurrency(optimizationResults.statistics.maxNPV)}</p>
                </div>
                <div>
                  <p className="text-gray-600">Potential Savings</p>
                  <p className="font-semibold text-blue-600">{formatCurrency(optimizationResults.statistics.costSavings)} ({optimizationResults.statistics.costSavingsPercent}%)</p>
                </div>
              </div>
            </div>

            {/* Comparison Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <h3 className="text-sm font-semibold text-gray-900 p-4 border-b">All Tested Thresholds</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Threshold (%)</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">NPV</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total Cost</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Repairs</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {optimizationResults.allResults.map((result, idx) => (
                      <tr key={idx} className={result.isOptimal ? 'bg-green-50' : ''}>
                        <td className="px-4 py-2 text-sm font-medium">{result.decayThreshold}%</td>
                        <td className="px-4 py-2 text-sm">{formatCurrency(result.npv)}</td>
                        <td className="px-4 py-2 text-sm">{formatCurrency(result.totalCost)}</td>
                        <td className="px-4 py-2 text-sm">{result.repairCount || 0}</td>
                        <td className="px-4 py-2">
                          {result.isOptimal ? (
                            <span className="px-2 py-1 bg-green-600 text-white text-xs font-semibold rounded">Optimal</span>
                          ) : (
                            <span className="text-xs text-gray-500">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Simulation Results */}
      {simulationResults && (
        <div id="simulation-results" className="space-y-6">
          {/* Export Buttons */}
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {optimizationResults ? 'Optimal Strategy Results' : 'Simulation Results'}
              </h2>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => exportSimulationToExcel(simulationResults, simulationResults.hierarchy, selectedBridge, showDecayPercent)}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                  title="Export to Excel"
                >
                  <FontAwesomeIcon icon={faFileExcel} className="mr-2" />
                  Export Excel
                </button>
                <button
                  onClick={() => exportSimulationToPDFUtil(simulationResults, simulationResults.hierarchy, selectedBridge, showDecayPercent)}
                  className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                  title="Export to PDF"
                >
                  <FontAwesomeIcon icon={faFilePdf} className="mr-2" />
                  Export PDF
                </button>
              </div>
            </div>
          </div>

          {/* Monte Carlo Results (if enabled) */}
          {simulationResults.mode === 'monte_carlo' && (
            <MonteCarloResults results={simulationResults} />
          )}

          {/* Summary Cards (for deterministic or as overview for Monte Carlo) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <FontAwesomeIcon icon={faLayerGroup} className="text-2xl text-blue-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Spans</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {simulationResults.spans?.length || 0}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <FontAwesomeIcon icon={faChartLine} className="text-2xl text-green-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-600">Time Horizon</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {scenario.timeHorizonYears} Years
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <FontAwesomeIcon icon={faDollarSign} className="text-2xl text-purple-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Cost</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(simulationResults.summary?.totalCost || 0)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Simulation Summary (Deterministic mode only) */}
          {simulationResults.mode !== 'monte_carlo' && (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Simulation Summary
                </h2>
                <div className="text-sm text-gray-600 mb-4">
                  Note: These costs do not include Mobilization or Periodic Inspection Costs
                  {simulationResults.simulationStartYear != null && (
                    <span className="block mt-1">Simulation start year: {simulationResults.simulationStartYear} (Year 1 = {simulationResults.simulationStartYear})</span>
                  )}
                </div>
                
                {simulationResults.summary && (
                <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-blue-800">Total Cost ({scenario.timeHorizonYears} Years)</h3>
                    <p className="text-2xl font-bold text-blue-900">
                      {formatCurrency(simulationResults.summary.totalCost)}
                    </p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-green-800">Inflation-Adjusted Total</h3>
                    <p className="text-2xl font-bold text-green-900">
                      {formatCurrency(simulationResults.summary.npv)}
                    </p>
                    {simulationResults.summary.calculationLog && (
                      <p className="text-xs text-green-700 mt-1">
                        Inflation Rate: {(simulationResults.summary.calculationLog.inflationRate * 100).toFixed(2)}%
                      </p>
                    )}
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-purple-800">Average Yearly Cost</h3>
                    <p className="text-2xl font-bold text-purple-900">
                      {formatCurrency(simulationResults.summary.avgYearlyCost)}
                    </p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-orange-800">Peak Cost Year</h3>
                    <p className="text-2xl font-bold text-orange-900">
                      Year {simulationResults.summary.peakCostYear?.year || 'N/A'}
                    </p>
                  </div>
                </div>
                
                {/* Summary Calculation Log */}
                {simulationResults.summary.calculationLog && (
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Summary Calculation Details</h3>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">Inflation Rate: </span>
                        <span className="text-gray-600">{(simulationResults.summary.calculationLog.inflationRate * 100).toFixed(2)}%</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Explanation: </span>
                        <span className="text-gray-600">{simulationResults.summary.calculationLog.explanation}</span>
                      </div>
                      {simulationResults.summary.calculationLog.yearlyBreakdown && simulationResults.summary.calculationLog.yearlyBreakdown.length > 0 && (
                        <div className="mt-4">
                          <span className="font-medium text-gray-700 block mb-2">Yearly Inflation-Adjusted Breakdown:</span>
                          <div className="max-h-48 overflow-y-auto">
                            <table className="min-w-full divide-y divide-gray-200 text-xs">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="px-2 py-1 text-left">Year</th>
                                  <th className="px-2 py-1 text-right">Base Cost</th>
                                  <th className="px-2 py-1 text-right">Inflation Factor</th>
                                  <th className="px-2 py-1 text-right">Inflated Cost</th>
                                  <th className="px-2 py-1 text-left">Formula</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {simulationResults.summary.calculationLog.yearlyBreakdown.map((year, idx) => (
                                  <tr key={idx}>
                                    <td className="px-2 py-1">{year.year}</td>
                                    <td className="px-2 py-1 text-right">{formatCurrency(parseFloat(year.baseCost))}</td>
                                    <td className="px-2 py-1 text-right font-mono">{year.inflationFactor}</td>
                                    <td className="px-2 py-1 text-right font-semibold">{formatCurrency(parseFloat(year.inflatedCost))}</td>
                                    <td className="px-2 py-1 text-left font-mono text-xs">{year.formula}</td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot className="bg-gray-100 font-semibold">
                                <tr>
                                  <td className="px-2 py-1">Total</td>
                                  <td className="px-2 py-1 text-right">{formatCurrency(simulationResults.summary.calculationLog.totalBaseCost)}</td>
                                  <td className="px-2 py-1 text-right">-</td>
                                  <td className="px-2 py-1 text-right">{formatCurrency(simulationResults.summary.calculationLog.totalInflatedCost)}</td>
                                  <td className="px-2 py-1"></td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                </>
              )}
            </div>
            </div>
          )}

          {/* Calculation Log Panel */}
          {simulationResults.mode !== 'monte_carlo' && (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-6">
                <button
                  onClick={() => setIsCalculationDetailsCollapsed(!isCalculationDetailsCollapsed)}
                  className="flex items-center justify-between w-full text-left mb-4"
                >
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <FontAwesomeIcon icon={faWrench} className="text-blue-600" />
                    Calculation Log
                  </h2>
                  <FontAwesomeIcon 
                    icon={isCalculationDetailsCollapsed ? faChevronRight : faChevronDown} 
                    className="text-gray-400"
                  />
                </button>
                
                {!isCalculationDetailsCollapsed && (
                  <div className="mt-4 space-y-4">
                    <div className="text-sm text-gray-600 mb-4">
                      Detailed calculation steps and formulas used in the simulation. This log helps verify the calculation process.
                    </div>
                    
                    {/* Extract calculation logs from yearly results */}
                    {simulationResults.yearlyResults && simulationResults.yearlyResults.length > 0 && (
                      <div className="space-y-6 max-h-96 overflow-y-auto">
                        {simulationResults.yearlyResults
                          .filter(yr => yr.spanResults && yr.spanResults.some(sr => sr.calculationDetails))
                          .map((yearResult, yearIdx) => (
                            <div key={yearIdx} className="border-l-4 border-blue-500 pl-4">
                              <h3 className="font-semibold text-gray-900 mb-2">
                                Year {yearResult.year}
                              </h3>
                              
                              {yearResult.spanResults
                                .filter(sr => sr.calculationDetails && sr.totalCost > 0)
                                .map((spanResult, spanIdx) => (
                                  <div key={spanIdx} className="mb-4 ml-4 p-3 bg-gray-50 rounded">
                                    <div className="font-medium text-gray-800 mb-2">
                                      {spanResult.spanCode || spanResult.spanName || `Element ${spanResult.spanId}`}
                                    </div>
                                    
                                    {spanResult.calculationDetails && (
                                      <div className="text-sm space-y-2">
                                        <div>
                                          <span className="font-medium text-gray-700">Input Values:</span>
                                          <ul className="ml-4 mt-1 space-y-1 text-gray-600">
                                            <li>Total Area: {spanResult.calculationDetails.totalArea} m²</li>
                                            <li>Corrosion Category: {spanResult.calculationDetails.corrosionCategory || 'N/A'}</li>
                                            <li>Damage Level: {spanResult.calculationDetails.damageLevel || 'N/A'}</li>
                                            <li>Damage Split: A={spanResult.calculationDetails.damageSplit?.A || 0}%, B={spanResult.calculationDetails.damageSplit?.B || 0}%, C={spanResult.calculationDetails.damageSplit?.C || 0}%</li>
                                            <li>Confined Space Multiplier: {spanResult.calculationDetails.confinedSpaceMultiplier || 1}</li>
                                            <li>Years Since Last Repair: {spanResult.yearsSinceRepair || 0}</li>
                                          </ul>
                                        </div>
                                        
                                        {spanResult.calculationDetails.degradationFormula && (
                                          <div>
                                            <span className="font-medium text-gray-700">Degradation Calculation:</span>
                                            <div className="ml-4 mt-1 text-gray-600 font-mono text-xs">
                                              {spanResult.calculationDetails.degradationFormula}
                                            </div>
                                            <div className="ml-4 mt-1 text-gray-600">
                                              Result: {spanResult.calculationDetails.degradationResult?.toFixed(2) || 0}%
                                            </div>
                                          </div>
                                        )}
                                        
                                        <div>
                                          <span className="font-medium text-gray-700">Repair Decision:</span>
                                          <ul className="ml-4 mt-1 space-y-1 text-gray-600">
                                            <li>Decay Threshold: {spanResult.calculationDetails.decayThreshold || 0}%</li>
                                            <li>Threshold Reached: {spanResult.calculationDetails.thresholdReached ? 'Yes' : 'No'}</li>
                                            {spanResult.calculationDetails.degradedAreaCalculation && (
                                              <li>Degraded Area: {spanResult.calculationDetails.degradedAreaCalculation}</li>
                                            )}
                                          </ul>
                                        </div>
                                        
                                        {spanResult.materialCost > 0 && (
                                          <div>
                                            <span className="font-medium text-gray-700">Cost Breakdown:</span>
                                            <ul className="ml-4 mt-1 space-y-1 text-gray-600">
                                              <li>Material Cost: {formatCurrency(spanResult.materialCost)}</li>
                                              <li>Labor Cost: {formatCurrency(spanResult.laborCost || 0)}</li>
                                              <li>Washing Cost: {formatCurrency(spanResult.washingCost || 0)}</li>
                                              <li>Total Cost: {formatCurrency(spanResult.totalCost)}</li>
                                            </ul>
                                          </div>
                                        )}
                                        
                                        {spanResult.repairType && (
                                          <div>
                                            <span className="font-medium text-gray-700">Repair Type:</span>
                                            <span className="ml-2 text-gray-600 capitalize">{spanResult.repairType}</span>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                            </div>
                          ))}
                      </div>
                    )}
                    
                    {(!simulationResults.yearlyResults || 
                      !simulationResults.yearlyResults.some(yr => yr.spanResults && yr.spanResults.some(sr => sr.calculationDetails))) && (
                      <div className="text-center py-8 text-gray-500">
                        <p>No calculation details available for this simulation.</p>
                        <p className="text-sm mt-2">Calculation logs are generated when repairs are triggered.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Detailed Results Table (Deterministic mode only) */}
          {simulationResults.mode !== 'monte_carlo' && (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">
                    4. Detailed Results Table
                  </h2>
                </div>
                <div className="text-sm text-gray-600 mb-4">
                  Hierarchical breakdown of simulation results by bridge, span, and repair process. 
                  Years are shown as columns with costs for each activity.
                </div>
                
                {/* Search and Filter Controls */}
                <div className="mb-4 flex items-center space-x-4">
                  <div className="flex-1 relative">
                    <FontAwesomeIcon 
                      icon={faSearch} 
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" 
                    />
                    <input
                      type="text"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Search by asset, span, panel bay, or activity..."
                      value={resultsSearchTerm}
                      onChange={(e) => setResultsSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <FontAwesomeIcon icon={faFilter} className="text-gray-400" />
                    <select
                      value={resultsFilterType}
                      onChange={(e) => setResultsFilterType(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">All Types</option>
                      <option value="bridge">Bridge</option>
                      <option value="asset">Asset</option>
                      <option value="span">Span</option>
                      <option value="panelbay">Panel Bay</option>
                      <option value="maintenance">Maintenance</option>
                      <option value="activity">Activity</option>
                    </select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showDecayPercent}
                        onChange={(e) => setShowDecayPercent(e.target.checked)}
                        className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">Show Decay %</span>
                    </label>
                  </div>
                  {(resultsSearchTerm || resultsFilterType !== 'all') && (
                    <button
                      onClick={() => {
                        setResultsSearchTerm('');
                        setResultsFilterType('all');
                      }}
                      className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
                    >
                      Clear
                    </button>
                  )}
                </div>
                
                <SimulationResultsTable
                  data={simulationResults.hierarchy || null}
                  expandedItems={expandedItems}
                  onToggleExpanded={toggleExpanded}
                  searchTerm={resultsSearchTerm}
                  filterType={resultsFilterType}
                  showDecayPercent={showDecayPercent}
                  simulationResults={simulationResults}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Repair Project Modal */}
      {showCreateProjectModal && simulationResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Create Repair Project from Simulation</h2>
                <button
                  onClick={() => setShowCreateProjectModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FontAwesomeIcon icon={faTimesCircle} className="text-2xl" />
                </button>
              </div>

              {/* Project Details Form */}
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Project Name *</label>
                  <input
                    type="text"
                    value={projectFormData.projectName}
                    onChange={(e) => setProjectFormData(prev => ({ ...prev, projectName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Bridge Maintenance 2025"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Project Code</label>
                    <input
                      type="text"
                      value={projectFormData.projectCode}
                      onChange={(e) => setProjectFormData(prev => ({ ...prev, projectCode: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Auto-generated if empty"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Planned Year *</label>
                    <input
                      type="number"
                      value={projectFormData.plannedYear}
                      onChange={(e) => setProjectFormData(prev => ({ ...prev, plannedYear: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      min={new Date().getFullYear()}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Crews Count</label>
                    <input
                      type="number"
                      value={projectFormData.crewsCount}
                      onChange={(e) => setProjectFormData(prev => ({ ...prev, crewsCount: parseInt(e.target.value) || 1 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                    <select
                      value={projectFormData.priority}
                      onChange={(e) => setProjectFormData(prev => ({ ...prev, priority: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={projectFormData.description}
                    onChange={(e) => setProjectFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                    placeholder="Project description..."
                  />
                </div>
              </div>

              {/* Project Summary */}
              {projectFormData.plannedYear && (() => {
                const plannedYear = projectFormData.plannedYear;
                const startYear = simulationResults.simulationStartYear != null && simulationResults.simulationStartYear > 0
                  ? simulationResults.simulationStartYear
                  : new Date().getFullYear();
                const yearIndex = plannedYear - startYear + 1;
                const yearResult = simulationResults.yearlyResults?.find(yr => yr.year === yearIndex);
                const hasRepairs = yearResult && yearResult.totalCost > 0;
                const elementCount = yearResult?.spanResults?.filter(sr => sr.totalCost > 0).length || 0;
                
                return (
                  <div className={`border rounded-md p-4 mb-6 ${hasRepairs ? 'bg-blue-50 border-blue-200' : 'bg-yellow-50 border-yellow-200'}`}>
                    <h3 className="font-semibold text-gray-900 mb-2">Project Summary</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Planned Year:</span>
                        <span className="ml-2 font-medium">{plannedYear}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Elements with Repairs:</span>
                        <span className="ml-2 font-medium">{elementCount}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Estimated Cost:</span>
                        <span className="ml-2 font-medium">
                          {hasRepairs ? `$${(yearResult.totalCost || 0).toLocaleString()}` : '$0'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Bridge:</span>
                        <span className="ml-2 font-medium">{selectedBridge?.name || 'N/A'}</span>
                      </div>
                    </div>
                    {!hasRepairs && (
                      <p className="text-sm text-yellow-700 mt-2">
                        ⚠️ No repairs found for year {plannedYear}. Please select a different year or check the simulation results.
                      </p>
                    )}
                  </div>
                );
              })()}

              {/* Actions */}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowCreateProjectModal(false);
                    setProjectFormData({
                      projectName: '',
                      projectCode: '',
                      description: '',
                      plannedYear: new Date().getFullYear(),
                      crewsCount: 1,
                      priority: 'medium'
                    });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateRepairProject}
                  disabled={!projectFormData.projectName || !projectFormData.plannedYear}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create Project
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedSimulationResults;